import { Box } from '@mui/material';
import { WidgetWrapper } from '../WidgetWrapper';
import { isAlarmLevelValid, transferValueToNewRange } from '../../utils';
import {
  cardGraphicLabel,
  cardGraphicLabelLeft,
  cardGraphicLabelRight,
  cardTowerGraphic,
  cardTowerGraphicAnimation,
  cardTowerGraphicLevel,
  cardGraphicText,
  cardTowerGraphicAnimationWrapper,
} from '../style';
import { AlarmType } from '../../types';
import { WET_WELL_TAG_NAME } from 'src/features/sites';
import { AssetType } from 'src/types/assets';
import { useGetLatestGatewayReadings } from 'src/hooks/useGetLatestGatewayReadings';
import { TowerIcon } from 'src/assets/icons/TowerIcon';

type Props = {
  title: string;
  height: number;
  waterLevel: number | null;
  unit: string;
  alarms: AlarmType[];
  isEditMode?: boolean;
  widgetId?: string;
  toggleAddOrRemoveHandler?: () => void;
  testId?: string;
  asset: AssetType | undefined;
  timestamp: string | null;
};

export const WaterTower = ({
  title,
  height,
  waterLevel,
  unit,
  alarms,
  isEditMode,
  widgetId,
  toggleAddOrRemoveHandler,
  testId,
  asset,
  timestamp,
}: Props) => {
  const handleAlarmsView = () =>
    alarms.map((alarm, index) => (
      <Box
        display="span"
        key={index}
        sx={{
          bottom: `${transferValueToNewRange(0, height, 0, HEIGHT_DISPLAY, alarm.level)}%`,
          ...(isAlarmLevelValid(alarm) ? labelRightStyle : {}),
        }}
      >
        {isAlarmLevelValid(alarm) ? alarm.alarmTagName : ''}
      </Box>
    ));

  const waterTowerAsset = asset?.assignedTags?.find(
    (tag) => tag?.tagName?.toLowerCase() === WET_WELL_TAG_NAME
  );

  const assignedTagId = waterTowerAsset?.assignedTagId;
  const gatewayId = waterTowerAsset?.assignedGatewayId;

  const { readings: latestReadings } = useGetLatestGatewayReadings({
    gatewayId: gatewayId,
    tagId: assignedTagId,
  });

  const readingsRawInputName = assignedTagId && latestReadings[assignedTagId];
  const readingsValues = readingsRawInputName?.values;
  const readingValueList = readingsValues && Object.values(readingsValues);
  const readingKeysList = readingsValues && Object.keys(readingsValues);

  const readingValue = readingValueList && readingValueList[0];
  const roundedWaterLevel = readingValue !== false ? readingValue : -1;
  const initialLatestTimestamp = readingKeysList?.length && readingKeysList[0];

  const HEIGHT_DISPLAY = 100;
  const waterLevelData =
    waterLevel !== null && waterLevel !== undefined ? waterLevel : Number(roundedWaterLevel);

  const validLevel = waterLevelData !== null && waterLevelData !== -1 && !isNaN(waterLevelData);
  const waterLevelDisplay = validLevel
    ? transferValueToNewRange(0, height, 0, HEIGHT_DISPLAY, waterLevelData)
    : null;

  const labelLeftStyle = { ...cardGraphicLabel, ...cardGraphicLabelLeft, right: 'calc(100%)' };
  const labelRightStyle = {
    ...cardGraphicLabel,
    ...cardGraphicLabelRight,
    left: 'calc(100% + 10px)',
  };

  const displayWaterLevelData = () => {
    if (!isNaN(waterLevelData) && validLevel) {
      return waterLevelData;
    } else {
      return '';
    }
  };

  return (
    <WidgetWrapper
      testId={testId}
      title={title}
      isEditMode={isEditMode}
      widgetId={widgetId!}
      toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
      isDataReady={waterLevelData !== null}
      timestamp={timestamp || initialLatestTimestamp}
    >
      <Box
        display="div"
        sx={cardTowerGraphic}
        data-sm={waterLevelData ? 'data-available' : 'no-data'}
      >
        <Box display="div" sx={cardTowerGraphicAnimationWrapper}>
          <Box display="span" sx={{ bottom: '100%', ...labelLeftStyle }}>
            {height} {unit}
          </Box>
          <Box display="span" sx={{ bottom: '0%', ...labelLeftStyle }}>
            0 {unit}
          </Box>
          <Box
            display="p"
            sx={() => cardGraphicText(waterLevelDisplay)}
            data-sm="water-tower-value"
          >
            {displayWaterLevelData()} {unit}
          </Box>
          {handleAlarmsView()}
        </Box>

        <Box display="div" sx={cardTowerGraphicAnimation}>
          <Box display="span" sx={() => cardTowerGraphicLevel(waterLevelDisplay)} />
        </Box>
        <TowerIcon waterLevel={waterLevelDisplay} />
      </Box>
    </WidgetWrapper>
  );
};
